// Port of CoreModels/Geo/SolarCalculator.swift: offline sunrise/sunset (NOAA simplified algorithm). Accuracy ±2 min at
// Alpine latitudes; terrain shading is not modelled (known limitation).

import type { EpochMs, GeoCoordinate } from './models';
import { degreesToRadians, radiansToDegrees } from './geo';

export interface DayLight {
  sunrise?: EpochMs;
  sunset?: EpochMs;
  /** Civil dusk (sun 6° below the horizon) — last practical light for pitching a tent. */
  civilDusk?: EpochMs;
  civilDawn?: EpochMs;
  isPolarDay: boolean;
  isPolarNight: boolean;
}

type EventResult = { kind: 'time'; minutesUTC: number } | { kind: 'alwaysUp' } | { kind: 'alwaysDown' };

function eventTime(dayOfYear: number, coordinate: GeoCoordinate, zenith: number, rising: boolean): EventResult {
  const gamma = ((2 * Math.PI) / 365) * (dayOfYear - 1 + 0.5);
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(gamma) -
      0.032077 * Math.sin(gamma) -
      0.014615 * Math.cos(2 * gamma) -
      0.040849 * Math.sin(2 * gamma));
  const decl =
    0.006918 -
    0.399912 * Math.cos(gamma) +
    0.070257 * Math.sin(gamma) -
    0.006758 * Math.cos(2 * gamma) +
    0.000907 * Math.sin(2 * gamma) -
    0.002697 * Math.cos(3 * gamma) +
    0.00148 * Math.sin(3 * gamma);
  const latRad = degreesToRadians(coordinate.latitude);
  const zenRad = degreesToRadians(zenith);
  const cosH = Math.cos(zenRad) / (Math.cos(latRad) * Math.cos(decl)) - Math.tan(latRad) * Math.tan(decl);
  if (cosH > 1) return { kind: 'alwaysDown' };
  if (cosH < -1) return { kind: 'alwaysUp' };
  const ha = radiansToDegrees(Math.acos(cosH));
  const signed = rising ? ha : -ha;
  return { kind: 'time', minutesUTC: 720 - 4 * (coordinate.longitude + signed) - eqTime };
}

/** Daylight times for the UTC calendar day containing `date` at `coordinate`. */
export function daylight(date: EpochMs, coordinate: GeoCoordinate): DayLight {
  const d = new Date(date);
  const dayStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  const dayOfYear = Math.round((dayStart - yearStart) / 86_400_000) + 1;
  const toDate = (r: EventResult) => (r.kind === 'time' ? dayStart + r.minutesUTC * 60_000 : undefined);
  const rise = eventTime(dayOfYear, coordinate, 90.833, true);
  return {
    sunrise: toDate(rise),
    sunset: toDate(eventTime(dayOfYear, coordinate, 90.833, false)),
    civilDusk: toDate(eventTime(dayOfYear, coordinate, 96, false)),
    civilDawn: toDate(eventTime(dayOfYear, coordinate, 96, true)),
    isPolarDay: rise.kind === 'alwaysUp',
    isPolarNight: rise.kind === 'alwaysDown',
  };
}
